import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { toast } from "sonner";
import { Shield, User, Building2, Briefcase, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const roles = [
  { id: "investor", label: "Investor", icon: Briefcase, description: "Executive portfolio access" },
  { id: "administrator", label: "Administrator", icon: Shield, description: "HR & Admin Portal" },
  { id: "vendor", label: "Vendor", icon: Building2, description: "Vendor portal access" }
];

const LoginPage = () => {
  const [selectedRole, setSelectedRole] = useState(null);
  const [userCode, setUserCode] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleRoleSelect = (roleId) => {
    if (roleId === "administrator") {
      // Redirect to Admin login page
      navigate("/admin-login");
      return;
    }
    setSelectedRole(roleId);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!selectedRole) {
      toast.error("Please select a role");
      return;
    }
    
    if (!userCode || !password) {
      toast.error("Please enter user code and password");
      return;
    }

    setLoading(true);
    try {
      await login(userCode, password, selectedRole);
      toast.success("Login successful");
      navigate("/");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0b1c2d] flex" data-testid="login-page">
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: "url('https://images.unsplash.com/photo-1526778548025-fa2f459cd5c1?q=80&w=2000&auto=format&fit=crop')",
            filter: "brightness(0.3)"
          }}
        />
        <div className="relative z-10 flex flex-col justify-center px-12 text-white">
          <div className="mb-8">
            <h1 className="text-5xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <span className="text-[#c9a227]">CDACE</span>–ClemTech
            </h1>
            <p className="text-2xl text-slate-300">Integrated PGMO Platform</p>
          </div>
          <div className="space-y-4 text-slate-400">
            <p className="flex items-center gap-3">
              <span className="w-2 h-2 bg-[#c9a227] rounded-full"></span>
              M1 Funds & Trade Performance Management
            </p>
            <p className="flex items-center gap-3">
              <span className="w-2 h-2 bg-[#c9a227] rounded-full"></span>
              Multi-Region Portfolio Governance
            </p>
            <p className="flex items-center gap-3">
              <span className="w-2 h-2 bg-[#c9a227] rounded-full"></span>
              Executive-Grade Analytics & Compliance
            </p>
          </div>
          <div className="mt-12 pt-8 border-t border-slate-700">
            <p className="text-sm text-slate-500">
              Software Architecture Document Version 2.0 — February 2026
            </p>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <span className="text-[#c9a227]">CDACE</span>–ClemTech
            </h1>
            <p className="text-slate-400 mt-1">PGMO Platform</p>
          </div>

          <div className="glass-card p-8">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Welcome Back
              </h2>
              <p className="text-slate-400 mt-2">Select your role and sign in</p>
            </div>

            {/* Role Selection */}
            <div className="mb-6">
              <Label className="text-slate-300 mb-3 block">Select Role</Label>
              <div className="grid grid-cols-3 gap-3">
                {roles.map((role) => {
                  const Icon = role.icon;
                  return (
                    <button
                      key={role.id}
                      type="button"
                      data-testid={`role-${role.id}`}
                      onClick={() => handleRoleSelect(role.id)}
                      className={`p-4 rounded-sm border transition-all duration-200 flex flex-col items-center gap-2 ${
                        selectedRole === role.id
                          ? "border-[#c9a227] bg-[#c9a227]/10 text-[#c9a227]"
                          : "border-slate-700 bg-slate-800/50 text-slate-400 hover:border-slate-600"
                      }`}
                    >
                      <Icon className="w-6 h-6" />
                      <span className="text-xs font-medium">{role.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <form onSubmit={handleLogin} className="space-y-5">
              <div>
                <Label htmlFor="userCode" className="text-slate-300">User Code</Label>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <Input
                    id="userCode"
                    data-testid="user-code-input"
                    type="text"
                    placeholder="Enter user code"
                    value={userCode}
                    onChange={(e) => setUserCode(e.target.value)}
                    className="pl-10 bg-slate-950/50 border-[#c9a227]/20 focus:border-[#c9a227] text-white"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="password" className="text-slate-300">Password</Label>
                <div className="relative mt-1">
                  <Shield className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <Input
                    id="password"
                    data-testid="password-input"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10 bg-slate-950/50 border-[#c9a227]/20 focus:border-[#c9a227] text-white"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                data-testid="login-submit-btn"
                disabled={loading}
                className="w-full btn-primary h-12 text-base"
              >
                {loading ? "Signing in..." : "Sign In"}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-xs text-slate-500">
                Demo Credentials: User Code <span className="text-[#c9a227]">100001</span> / Password <span className="text-[#c9a227]">2026</span>
              </p>
            </div>
          </div>

          <div className="mt-6 text-center text-xs text-slate-500">
            <p>© 2026 CDACE–ClemTech. All rights reserved.</p>
            <p className="mt-1">Sovereign-Grade Infrastructure Management</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
